// src/components/CreateChurchDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Chip,
  Avatar,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getAvailableAdmins, createChurchWithAdmin } from '../services/egliseService';
import { AvailableAdmin, CreateChurchRequest } from '../types/church';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface CreateChurchDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateChurchDialog: React.FC<CreateChurchDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();

  // Form state
  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [adminUserId, setAdminUserId] = useState<number | ''>('');

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [availableAdmins, setAvailableAdmins] = useState<AvailableAdmin[]>([]);

  // Charger les admins disponibles quand le dialog s'ouvre
  useEffect(() => {
    if (open) {
      fetchAvailableAdmins();
    }
  }, [open]);

  const fetchAvailableAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const admins = await getAvailableAdmins();
      setAvailableAdmins(admins);
    } catch (err: any) {
      console.error('Failed to fetch available admins:', err);
      setError(err.response?.data?.detail || t('superadmin.churches.errorFetchingAdmins'));
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    // Validation
    if (!nom.trim()) {
      setError(t('superadmin.churches.errors.nameRequired'));
      return;
    }

    if (nom.trim().length < 2) {
      setError(t('superadmin.churches.errors.nameTooShort'));
      return;
    }

    if (nom.trim().length > 200) {
      setError(t('superadmin.churches.errors.nameTooLong'));
      return;
    }

    if (!adminUserId) {
      setError(t('superadmin.churches.errors.adminRequired'));
      return;
    }

    if (address && address.length > 500) {
      setError(t('superadmin.churches.errors.addressTooLong'));
      return;
    }

    if (phone && phone.length > 20) {
      setError(t('superadmin.churches.errors.phoneTooLong'));
      return;
    }

    if (email && email.length > 100) {
      setError(t('superadmin.churches.errors.emailTooLong'));
      return;
    }

    try {
      setLoading(true);
      const payload: CreateChurchRequest = {
        nom: nom.trim(),
        admin_user_id: adminUserId as number,
      };

      // Ajouter les champs optionnels seulement s'ils sont remplis
      if (description.trim()) payload.description = description.trim();
      if (address.trim()) payload.address = address.trim();
      if (phone.trim()) payload.phone = phone.trim();
      if (email.trim()) payload.email = email.trim();

      await createChurchWithAdmin(payload);
      resetForm();
      onSuccess();
    } catch (err: any) {
      console.error('Failed to create church:', err);
      setError(err.response?.data?.detail || t('superadmin.churches.errorCreating'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNom('');
    setDescription('');
    setAddress('');
    setPhone('');
    setEmail('');
    setAdminUserId('');
    setError(null);
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const selectedAdmin = availableAdmins.find(a => a.id === adminUserId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('superadmin.churches.createTitle')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Nom de l'église */}
          <TextField
            label={t('superadmin.churches.form.name')}
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            fullWidth
            required
            disabled={loading}
            placeholder="Église Orthodoxe Saint-Michel"
            helperText={t('superadmin.churches.form.nameHelper')}
          />

          {/* Description */}
          <TextField
            label={t('superadmin.churches.form.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
            disabled={loading}
            placeholder="Description de l'église..."
          />

          {/* Adresse */}
          <TextField
            label={t('superadmin.churches.form.address')}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            fullWidth
            disabled={loading}
            placeholder="123 rue de la Paix, 75001 Paris"
          />

          {/* Téléphone et Email sur la même ligne */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('superadmin.churches.form.phone')}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              fullWidth
              disabled={loading}
              placeholder="+33123456789"
            />
            <TextField
              label={t('superadmin.churches.form.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              disabled={loading}
              type="email"
              placeholder="contact@eglise.org"
            />
          </Box>

          {/* Sélection de l'administrateur */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              {t('superadmin.churches.form.selectAdmin')}
            </Typography>

            {loadingAdmins ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : availableAdmins.length === 0 ? (
              <Alert severity="warning">
                {t('superadmin.churches.noAvailableAdmins')}
              </Alert>
            ) : (
              <FormControl fullWidth required>
                <InputLabel>{t('superadmin.churches.form.admin')}</InputLabel>
                <Select
                  value={adminUserId}
                  onChange={(e) => setAdminUserId(e.target.value as number)}
                  label={t('superadmin.churches.form.admin')}
                  disabled={loading}
                >
                  {availableAdmins.map((admin) => (
                    <MenuItem key={admin.id} value={admin.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2">{admin.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {admin.email}
                          </Typography>
                        </Box>
                        {admin.verified && (
                          <CheckCircleIcon color="success" fontSize="small" />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Afficher les infos de l'admin sélectionné */}
            {selectedAdmin && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('superadmin.churches.form.selectedAdmin')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body1">{selectedAdmin.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedAdmin.email}
                    </Typography>
                    {selectedAdmin.phone && (
                      <Typography variant="body2" color="text.secondary">
                        {selectedAdmin.phone}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                    <Chip
                      label={selectedAdmin.role}
                      size="small"
                      variant="outlined"
                    />
                    {selectedAdmin.verified && (
                      <Chip
                        label={t('superadmin.churches.verified')}
                        size="small"
                        color="success"
                        icon={<CheckCircleIcon />}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || loadingAdmins || availableAdmins.length === 0}
        >
          {loading ? t('superadmin.churches.creating') : t('superadmin.churches.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateChurchDialog;
